import { P2GLink, P2GText } from '@parcel2go/core-ui';
import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { TranslationNamespaces } from '~/lib/constants';
import useGoto from '~/hooks/use-goto';
import { getCollectionStatus } from './getCollectionStatus';

interface props {
  item: SmartSendItemApiModel;
  variant?: 'body2' | 'caption';
  wrapper?: (content: ReactNode) => ReactNode;
}

/**
 * Compact collection-status line shown inside the order's service panel.
 * Renders nothing unless the booked order has an upcoming/just-missed collection.
 */
export default function ItemCollectionStatus(props: props) {
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const status = getCollectionStatus(
    props.item.BookingDetails?.EstimatedCollectionDate,
    props.item.SearchTrackingStatus,
    props.item.SearchIsDropOffService
  );

  if (!status) {
    return null;
  }

  const variant = props.variant ?? 'caption';
  const wrap = props.wrapper ?? ((content: ReactNode) => content);
  const rebookLink = <P2GLink variant={variant} onClick={() => goTo(RoutingConstants.Support)} />;

  if (status === 'tomorrow') {
    return wrap(
      <P2GText variant="body1" fontWeight="bold">
        {t('COLLECTION_STATUS_TOMORROW', { defaultValue: 'Collection scheduled for tomorrow' })}
      </P2GText>
    );
  }

  if (status === 'today') {
    return wrap(
      <P2GText variant="body1" fontWeight="bold">
        {t('COLLECTION_STATUS_TODAY', { defaultValue: 'Out for collection today' })}
      </P2GText>
    );
  }

  if (status === 'missed-yesterday') {
    return wrap(
      <P2GText variant="body1" fontWeight="bold">
        <Trans
          t={t}
          i18nKey="COLLECTION_STATUS_MISSED_YESTERDAY"
          defaults="Awaiting collection scan. Was it collected yesterday? <rebook>Rebook collection</rebook>"
          components={{ rebook: rebookLink }}
        />
      </P2GText>
    );
  }

  return wrap(
    <P2GText variant="body1" fontWeight="bold">
      <Trans
        t={t}
        i18nKey="COLLECTION_STATUS_MISSED"
        defaults="Awaiting collection scan. Not collected? <rebook>Rebook collection</rebook>"
        components={{ rebook: rebookLink }}
      />
    </P2GText>
  );
}
